import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const UserCreate = () => {
    useSessionValidation();

    const history = useHistory();
    const { isAuthenticated, companyId,branchId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [userNameStatus, setUserNameStatus] = useState(null); // null | 'available' | 'taken'

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
        }
    }, [isAuthenticated,branchId, companyId]);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userName, setUserName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [email, setEmail] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const generated = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
        const newUserName = generated.slice(0, 20);
        setUserName(newUserName);
        setUserNameStatus(null);
        if (newUserName) checkUserName(newUserName);
    }, [firstName, lastName]);

    const checkUserName = (value) => {
        if (!value || value.trim() === '') { setUserNameStatus(null); return; }
        const existing = JSON.parse(localStorage.getItem('existingUserNames') || '[]');
        setUserNameStatus(existing.includes(value.trim().toLowerCase()) ? 'taken' : 'available');
    };

    const handleUserNameChange = (e) => {
        const val = e.target.value.slice(0, 20);
        setUserName(val);
        checkUserName(val);
    };

    const clearForm = () => {
        setFirstName(''); setLastName(''); setUserName('');
        setContactNumber(''); setEmail('');
    };

    const submitData = async (e) => {
        e.preventDefault();
        if (!firstName) { setErrorMessage('First Name is required!'); return; }
        if (!lastName) { setErrorMessage('Last Name is required!'); return; }
        if (!contactNumber) { setErrorMessage('Contact Number is required!'); return; }
        if (!email) { setErrorMessage('Email is required!'); return; }
        if (userNameStatus === 'taken') { setErrorMessage('Username is already taken. Please choose a different one.'); return; }

        setIsLoading(true);
        const payload = {
            FirstName: firstName, LastName: lastName, UserName: userName,
            Email: email, ContactNumber: contactNumber,
            CreatedBy: userId, CompanyId: companyId,BranchId:branchId, StaffId: userId, CreatedDate: new Date(),
        };

        postData('UserApi','', payload, token)
            .then(() => {
                setSuccessMessage('User created successfully!');
                setErrorMessage('');
                clearForm();
                history.push('/UserIndex');
            })
            .catch(() => {
                setErrorMessage('Failed to create user.');
                setIsLoading(false);
            });
    };

    const inputStyle = {
        width: '100%', padding: '6px 10px', fontSize: '13px',
        borderRadius: '8px', border: '1px solid #e2e8f0',
        background: 'white', outline: 'none', fontFamily: 'inherit',
        boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: '#4a5568', marginBottom: '4px',
        textTransform: 'uppercase', letterSpacing: '0.3px'
    };

    const sectionStyle = {
        border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '12px', marginBottom: '10px', background: '#fafafa'
    };

    const sectionTitleStyle = {
        fontSize: '12px', fontWeight: '700', color: '#2C3E6E',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px', paddingBottom: '6px',
        borderBottom: '1px solid #e2e8f0'
    };

    return (
        <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Roman' }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
                        Create User
                    </h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            variant="contained"
                            onClick={submitData}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                background: '#2C3E6E', color: 'white', padding: '4px 12px',
                                borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save User'}
                        </Button>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/UserIndex"
                            startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                borderColor: '#2C3E6E', color: '#2C3E6E', padding: '4px 12px',
                                borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                            }}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {/* Personal Information */}
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...sectionTitleStyle }}>
                        <PersonIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Personal Information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                            <label style={labelStyle}>First Name *</label>
                            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} type="text" placeholder="Enter first name" maxLength={30} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Last Name *</label>
                            <input value={lastName} onChange={(e) => setLastName(e.target.value)} type="text" placeholder="Enter last name" maxLength={30} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>
                                User Name *
                                {userNameStatus === 'available' && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#11998e', fontWeight: '600', textTransform: 'none' }}>✓ Available</span>}
                                {userNameStatus === 'taken' && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#e53e3e', fontWeight: '600', textTransform: 'none' }}>✗ Already taken</span>}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    value={userName}
                                    onChange={handleUserNameChange}
                                    type="text"
                                    placeholder="Auto-generated, editable"
                                    maxLength={20}
                                    style={{
                                        ...inputStyle,
                                        paddingRight: '32px',
                                        border: userNameStatus === 'taken'
                                            ? '1px solid #e53e3e'
                                            : userNameStatus === 'available'
                                            ? '1px solid #11998e'
                                            : '1px solid #e2e8f0'
                                    }}
                                />
                                <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {userNameStatus === 'available' && <CheckCircleIcon style={{ fontSize: '16px', color: '#11998e' }} />}
                                    {userNameStatus === 'taken' && <CancelIcon style={{ fontSize: '16px', color: '#e53e3e' }} />}
                                </div>
                            </div>
                            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>{userName.length}/20 characters</div>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...sectionTitleStyle }}>
                        <PhoneIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Contact Details
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        <div>
                            <label style={labelStyle}>Contact Number *</label>
                            <input value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} type="text" placeholder="Enter contact number" maxLength={15} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Email Address *</label>
                            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="user@example.com" maxLength={40} style={inputStyle} />
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserCreate;

